import type { JSX } from 'preact';

interface Feature {
  icon: JSX.Element;
  title: string;
  body: string;
}

const features: Feature[] = [
  {
    icon: (
      <svg width="22" height="22" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M18.178 8c5.096 0 5.096 8 0 8-5.095 0-7.133-8-12.739-8-4.585 0-4.585 8 0 8 5.606 0 7.644-8 12.74-8z" /></svg>
    ),
    title: 'Unlimited Tokens',
    body: 'No message caps, no rate limits, no per-token billing. Chat, reason, and build as much as you want — the counter never runs.',
  },
  {
    icon: (
      <svg width="22" height="22" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
    ),
    title: 'Pay for GPU Time',
    body: "You're billed for the active GPU time your session uses — never per token. Sessions share GPU capacity, so cost drops the more people are on.",
  },
  {
    icon: (
      <svg width="22" height="22" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M9 9h6v6H9z" /><path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3" /></svg>
    ),
    title: 'Real GPU Hardware',
    body: 'Your sessions run on real GPUs with a large model and a huge context window — not a throttled, shared cloud endpoint.',
  },
  {
    icon: (
      <svg width="22" height="22" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
    ),
    title: 'Agent Mode',
    body: 'Let your AI work autonomously — read files, run shell commands, search the web, and manage git. It thinks, acts, and reports back.',
  },
  {
    icon: (
      <svg width="22" height="22" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>
    ),
    title: 'Persistent Memory',
    body: 'A built-in vector database with RAG. Your AI remembers past conversations and retrieves relevant context automatically across sessions.',
  },
  {
    icon: (
      <svg width="22" height="22" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
    ),
    title: 'Or Run It Locally',
    body: 'Prefer to self-host? The same chat, agent mode, and memory ship as a single open-source binary you can run on your own hardware.',
  },
];

function Features() {
  return (
    <section class="section" id="features">
      <div class="container">
        <div class="section-header">
          <span class="tag">Capabilities</span>
          <h2>Unlimited AI, on real hardware</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              class="glass p-8 transition-[border-color,box-shadow] duration-300 hover:border-[oklch(25%_0.04_25)] hover:shadow-[0_0_30px_var(--accent-glow)]"
            >
              <div class="w-11 h-11 rounded-xl bg-accent-subtle border border-[oklch(55%_0.22_25/_0.2)] flex items-center justify-center mb-5 text-[1.2rem]">
                {f.icon}
              </div>
              <h3 class="font-display text-[1.1rem] font-bold mb-2 tracking-[-0.01em]">{f.title}</h3>
              <p class="text-muted text-[0.9rem] leading-[1.55]">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Features;
